import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';

export interface UserProfile {
  _id: string;
  email: string;
  username: string;
  displayName?: string;
  phone?: string;
  role: string;
  branchId?: {
    _id: string;
    name: string;
    code: string;
  };
  preferences?: {
    language?: 'en' | 'th' | 'zh';
    notifications?: {
      email?: boolean;
      push?: boolean;
      orderAlerts?: boolean;
      inventoryAlerts?: boolean;
    };
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UpdateProfileInput {
  displayName?: string;
  phone?: string | null;
  preferences?: {
    language?: 'en' | 'th' | 'zh';
    notifications?: {
      email?: boolean;
      push?: boolean;
      orderAlerts?: boolean;
      inventoryAlerts?: boolean;
    };
  };
}

interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
}

// Get current user profile
export function useProfile() {
  return useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ user: UserProfile }>>('/auth/profile');
      return response.data.data.user;
    },
  });
}

// Update profile
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateProfileInput) => {
      const response = await apiClient.patch<APIResponse<{ user: UserProfile }>>(
        '/auth/profile',
        data
      );
      return response.data.data.user;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['profile'], data);
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

// Change password
export function useChangePassword() {
  return useMutation({
    mutationFn: async (data: ChangePasswordInput) => {
      const response = await apiClient.patch<APIResponse<{ message: string }>>(
        '/auth/change-password',
        data
      );
      return response.data.data.message;
    },
  });
}

export type { UpdateProfileInput, ChangePasswordInput };
